import * as yup from 'yup';
import { merge } from 'lodash-es';
import { KPIAggFunc } from '../pages/PortfolioOverview/FundReporting/FundsKPIs';
import { FundProfileLinkRenderer } from '../pages/PortfolioOverview/components/OverviewTable/columns/TypeBasedConfigs/TypeCellRenderers/FundProfileLinkRenderer';
import { multiplierField, percentField, usdField, usdShortField } from './common-schema-defs';
import { companyFundMetricsFormSchema } from './CompanyFundMetrics.schema';

export function fundMetricsByFundFields() {
  return {
    activeCompanies: yup
      .array()
      .of(companyFundMetricsFormSchema())
      .nullable()
      .label('Active Companies')
      .default([]),
    date: yup.string().required().label('Date').default(''),
    fundId: yup.number().required().label('Fund').default(0),
    fundKind: yup.string().oneOf(['fund', 'intermediary', 'family']).default('fund').label('Fund Kind'),
    familyId: yup.number().nullable().default(null).label('Fund Family'),
    memberFundIds: yup.array().of(yup.number().required()).optional(),
    metrics: yup.object().shape(fundMetricsFields()).required().default({}),
    mostRecentContributedSecurities: usdField().nullable().default(null),
    mostRecentDeemedContributions: usdField().nullable().default(null),
    mostRecentGPTaxDistribution: usdField().nullable().default(null),
    totalCompanies: yup.number().default(0),
  };
}

export function fundMetricsByFundFormSchema() {
  return yup.object().shape(fundMetricsByFundFields());
}

export type FundMetricsByFund = yup.InferType<ReturnType<typeof fundMetricsByFundFormSchema>>;

export function createFundMetricsByFund(overrides: Partial<FundMetricsByFund> = {}): FundMetricsByFund {
  return merge({}, fundMetricsByFundFormSchema().getDefault(), overrides);
}

export function fundMetricsByFundGridSchema() {
  const { date, fundId } = fundMetricsByFundFields();
  return yup.object().shape({
    activeCompanies: yup.array().of(companyFundMetricsFormSchema()).required().default([]),
    date,
    fundId: fundId.gridMeta({
      cellRenderer: FundProfileLinkRenderer,
      formatter: 'fund',
    }),

    metrics: fundMetricsGridSchema(),
    totalCompanies: yup.number().required().default(0),
  });
}

export function fundMetricsFields() {
  return {
    activationDate: yup.date().nullable().default(null),
    amountAvailableForLpGpSplit: usdShortField()
      .nullable()
      .default(null)
      .label('Max Available for LP/GP Split'),
    amountAvailableForSecondaryReturn: usdShortField()
      .nullable()
      .default(null)
      .label('Max Available for Secondary Return'),
    availableForGpCatchup: usdShortField().nullable().default(null),
    availableForSuperReturn: yup.number().nullable().default(null),
    capitalCalledPercent: percentField().nullable().default(null),
    capitalLossRatio: multiplierField().nullable().default(null),
    contributions: usdShortField().required().label('Total Contributions').nullable().default(null),
    contributionsForCalculation: usdShortField().nullable().default(null),
    distributableProceeds: usdShortField()
      .required()
      .label('Distributable Proceeds')
      .nullable()
      .default(null),
    distributableProceedsPostGPCapital: usdShortField().nullable().default(null),
    distributableProceedsPostIdleFundsIncome: usdShortField().nullable().default(null),
    distributableProceedsPostManagementFee: usdShortField().nullable().default(null),
    distributableProceedsPostROC: usdShortField().nullable().default(null),
    distributableProceedsPostPreferredReturn: usdShortField().nullable().default(null),
    distributableProceedsPostSecondaryReturn: usdShortField().nullable().default(null),
    distributableProceedsPostSLPCapital: usdShortField().nullable().default(null),
    distributableProceedsUnadjusted: usdShortField()
      .required()
      .label('Distributable Proceeds (Unadjusted)')
      .nullable()
      .default(null),
    distributions: usdShortField().required().label('Total Distributions').nullable().default(null),
    dpi: multiplierField().required().label('DPI (Total)').nullable().default(null),
    escrowReceivable: usdShortField().nullable().default(null).label('Escrow Receivable'),
    fmv: usdShortField().label('Total Unrealized Value').nullable().default(null),
    followOnInvestmentAmount: usdShortField().nullable().default(null).label('Follow-On Investments'),
    gpCapital: usdShortField().nullable().default(null),
    gpContributions: usdShortField().required().label('GP Contributions').nullable().default(null),
    gpDistributions: usdShortField().required().label('GP Distributions').nullable().default(null),
    gpIncentive: usdShortField().required().label('GP Incentive (Carry)').nullable().default(null),
    gpIncentiveFromGpCatchup: usdShortField().nullable().default(null),
    gpIncentiveFromLpGpSplit: usdShortField().nullable().default(null),
    secondaryReturnGPIncentive: usdShortField().nullable().default(null),
    gpIncentiveFromSuperReturn: usdShortField().nullable().default(null),
    gpROC: usdShortField().nullable().default(null),
    grossIRR: percentField().nullable().label('Gross IRR').nullable().default(null),
    gpNavTotal: usdShortField().nullable().default(null),
    gpNonTaxDistribution: usdShortField().nullable().default(null),
    gpPortionTotal: usdShortField().nullable().default(null),
    gpPremiumCatchup: usdShortField().nullable().default(null),
    gpTotal: usdShortField().nullable().default(null),
    grossMOIC: multiplierField().nullable().label('Gross MOIC').nullable().default(null),
    idleFundsIncomeGP: usdShortField().nullable().default(null),
    idleFundsIncomeLP: usdShortField().nullable().default(null),
    initialInvestmentAmount: usdShortField().nullable().label('Initial Investment').nullable().default(null),
    investmentAmount: usdShortField().nullable().label('Total Invested').nullable().default(null),
    lossRatio: multiplierField().nullable().nullable().default(null),
    lpContributions: usdShortField().required().label('LP Contributions').nullable().default(null),
    lpDistributions: usdShortField().required().label('LP Distributions').nullable().default(null),
    lpDpi: multiplierField().required().label('DPI (LP)').nullable().default(null),
    lpGpSlitAmount: usdShortField().nullable().default(null).label('LP/GP Split Amount'),
    lpNav: usdShortField().required().label('LP NAV').nullable().default(null),
    lpNavFromLpGpSplit: usdShortField().nullable().default(null).label('LP NAV from LP/GP Split'),
    lpNavFromSuperReturn: usdShortField().nullable().default(null),
    lpNavTotal: usdShortField().nullable().default(null),
    lpNetIrr: percentField().required().label('Net IRR (LP)').nullable().default(null),
    lpROC: usdShortField().nullable().default(null),
    lpTvpi: multiplierField().nullable().default(null).label('TVPI (LP)'),
    managementFeeLP: usdShortField().nullable().default(null),
    navFromSecondaryReturnLP: usdShortField().nullable().default(null),
    navFromSecondaryReturnGP: usdShortField().nullable().default(null),
    navFromSuperReturnGP: usdShortField().nullable().default(null).label('NAV from Super Return (GP)'),
    navFromSuperReturnLP: usdShortField().nullable().default(null).label('NAV from Super Return (LP)'),
    navLPPortion: usdShortField().nullable().default(null),
    navGPPortion: usdShortField().nullable().default(null),
    managementFee: usdShortField().required().label('Management Fees').nullable().default(null),
    managementFeeRestored: usdShortField().nullable().default(null),
    maxGpCatchup: usdShortField().nullable().label('Max GP Catch Up').default(null),
    maxLpGpSplit: usdShortField().nullable().label('Max Available for LP/GP Split').default(null), // tier 1 (vs amountAvailableForLpGpSplit)
    navFromLpGpSplitGp: usdShortField().nullable().default(null).label('NAV from LP/GP Split (GP)'),
    navFromLpGpSplitLP: usdShortField().nullable().default(null).label('NAV from LP/GP Split (LP)'),
    netAssets: usdShortField().label('Working Capital').nullable().default(null).customMeta({
      description: 'Cash - Total Liabilities',
    }),
    netIRR: percentField().required().label('Net IRR (Total)').nullable().default(null),
    netMOIC: multiplierField().required().label('Net MOIC').nullable().default(null),
    netReturn: usdShortField().required().label('Net Return').nullable().default(null),
    profitAtSLPCapital: usdShortField().nullable().label('Profit at SLP Capital').nullable().default(null),
    realizedGL: usdShortField().nullable().label('Realized Gain/Loss').nullable().default(null),
    realizedValue: usdShortField().nullable().label('Total Realized Value').nullable().default(null),
    rvpi: multiplierField().required().label('RVPI (Total)').nullable().default(null),
    totalValue: usdShortField().required().label('Total Value').nullable().default(null),
    ttvpi: multiplierField().required().label('TVPI (Total)').nullable().default(null),
    unrealizedGL: usdShortField().nullable().label('Unrealized Gain/Loss').nullable().default(null),
    unrealizedValue: usdShortField().nullable().label('Total Unrealized Value').nullable().default(null),
  };
}

export function fundMetricsFormSchema() {
  return yup.object().shape(fundMetricsFields());
}

export function createFundMetrics(overrides: Partial<FundMetrics> = {}): FundMetrics {
  return merge({}, fundMetricsFormSchema().getDefault(), overrides);
}

export function fundMetricsGridSchema() {
  const baseFields = fundMetricsFields();
  return yup.object().shape({
    ...baseFields,
    amountAvailableForSecondaryReturn: baseFields.amountAvailableForSecondaryReturn.gridMeta({
      formatter: 'currency0Dp',
    }),
    availableForGpCatchup: baseFields.availableForGpCatchup.gridMeta({
      formatter: 'currency0Dp',
    }),
    availableForSuperReturn: baseFields.availableForSuperReturn.gridMeta({
      formatter: 'currency0Dp',
    }),
    contributions: baseFields.contributions.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributions: baseFields.distributions.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    distributableProceeds: baseFields.distributableProceeds.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributableProceedsPostGPCapital: baseFields.distributableProceedsPostGPCapital.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributableProceedsPostIdleFundsIncome: baseFields.distributableProceedsPostIdleFundsIncome.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributableProceedsPostManagementFee: baseFields.distributableProceedsPostManagementFee.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributableProceedsPostPreferredReturn: baseFields.distributableProceedsPostPreferredReturn.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributableProceedsPostSecondaryReturn: baseFields.distributableProceedsPostSecondaryReturn.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributableProceedsPostROC: baseFields.distributableProceedsPostROC.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributableProceedsPostSLPCapital: baseFields.distributableProceedsPostSLPCapital.gridMeta({
      formatter: 'currency0Dp',
    }),
    distributableProceedsUnadjusted: baseFields.distributableProceedsUnadjusted.gridMeta({
      formatter: 'currency0Dp',
    }),
    dpi: baseFields.dpi.gridMeta({
      formatter: 'multiplier',
      aggFunc: KPIAggFunc.dpi,
    }),
    escrowReceivable: baseFields.escrowReceivable.gridMeta({
      formatter: 'currency0Dp',
    }),
    fmv: baseFields.fmv.gridMeta({
      formatter: 'currency0Dp',
    }),
    gpCapital: baseFields.gpCapital.gridMeta({
      formatter: 'currency0Dp',
    }),
    gpContributions: baseFields.gpContributions.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    gpDistributions: baseFields.gpDistributions.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    gpIncentive: baseFields.gpIncentive.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    gpIncentiveSecondaryReturn: baseFields.secondaryReturnGPIncentive.gridMeta({
      formatter: 'currency0Dp',
    }),
    gpNonTaxDistribution: baseFields.gpNonTaxDistribution.gridMeta({
      formatter: 'currency0Dp',
    }),
    gpPortionTotal: baseFields.gpPortionTotal.gridMeta({
      formatter: 'currency0Dp',
    }),
    gpPremiumCatchup: baseFields.gpPremiumCatchup.gridMeta({
      formatter: 'currency0Dp',
    }),
    gpROC: baseFields.gpROC.gridMeta({
      formatter: 'currency0Dp',
    }),
    idleFundsIncomeGP: baseFields.idleFundsIncomeGP.gridMeta({
      formatter: 'currency0Dp',
    }),
    idleFundsIncomeLP: baseFields.idleFundsIncomeLP.gridMeta({
      formatter: 'currency0Dp',
    }),

    lpContributions: baseFields.lpContributions.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    lpDistributions: baseFields.lpDistributions.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    lpNav: baseFields.lpNav.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    lpNavTotal: baseFields.lpNavTotal.gridMeta({
      formatter: 'currency0Dp',
    }),
    lpROC: baseFields.lpROC.gridMeta({
      formatter: 'currency0Dp',
    }),
    managementFee: baseFields.managementFee.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    managementFeeLP: baseFields.managementFeeLP.gridMeta({
      formatter: 'currency0Dp',
    }),
    managementFeeRestored: baseFields.managementFeeRestored.gridMeta({
      formatter: 'currency0Dp',
    }),
    navFromLpGpSplitGp: baseFields.navFromLpGpSplitGp.gridMeta({
      formatter: 'currency0Dp',
    }),
    navFromLpGpSplitLP: baseFields.navFromLpGpSplitLP.gridMeta({
      formatter: 'currency0Dp',
    }),
    navFromSecondaryReturnGP: baseFields.navFromSecondaryReturnGP.gridMeta({
      formatter: 'currency0Dp',
    }),
    navFromSecondaryReturnLP: baseFields.navFromSecondaryReturnLP.gridMeta({
      formatter: 'currency0Dp',
    }),
    navFromSuperReturnGP: baseFields.navFromSuperReturnGP.gridMeta({
      formatter: 'currency0Dp',
    }),
    navFromSuperReturnLP: baseFields.navFromSuperReturnLP.gridMeta({
      formatter: 'currency0Dp',
    }),

    netAssets: baseFields.netAssets.gridMeta({
      formatter: 'currency0Dp',
    }),
    netIRR: baseFields.netIRR.gridMeta({
      formatter: 'percent',
      aggFunc: KPIAggFunc.sum,
    }),
    netMOIC: baseFields.netMOIC.gridMeta({
      formatter: 'multiplier',
      aggFunc: KPIAggFunc.moic,
    }),
    netReturn: baseFields.netReturn.gridMeta({
      formatter: 'currency0Dp',
      aggFunc: KPIAggFunc.sum,
    }),
    profitAtSLPCapital: baseFields.profitAtSLPCapital.gridMeta({
      formatter: 'currency0Dp',
    }),
    rvpi: baseFields.rvpi.gridMeta({
      formatter: 'multiplier',
      aggFunc: KPIAggFunc.rvpi,
    }),
    totalValue: baseFields.totalValue.gridMeta({
      formatter: 'currency0Dp',
    }),
    ttvpi: baseFields.ttvpi.gridMeta({
      formatter: 'multiplier',
      aggFunc: KPIAggFunc.tvpi,
    }),
  });
}

export function fundMetricsCalcsSchema() {
  const baseFields = fundMetricsFields();
  return yup.object().shape({
    ...baseFields,
    amountAvailableForLpGpSplit: baseFields.amountAvailableForLpGpSplit.customMeta({
      formatter: 'currency',
    }),
    amountAvailableForSecondaryReturn: baseFields.amountAvailableForSecondaryReturn.customMeta({
      formatter: 'currency',
    }),
    availableForGpCatchup: baseFields.availableForGpCatchup.customMeta({
      formatter: 'currency',
    }),
    availableForSuperReturn: baseFields.availableForSuperReturn.customMeta({
      formatter: 'currency',
    }),
    contributions: baseFields.contributions.customMeta({
      formatter: 'currency',
    }),
    distributableProceeds: baseFields.distributableProceeds.customMeta({
      formatter: 'currency',
    }),
    distributableProceedsPostGPCapital: baseFields.distributableProceedsPostGPCapital.customMeta({
      formatter: 'currency',
    }),
    distributableProceedsPostIdleFundsIncome: baseFields.distributableProceedsPostIdleFundsIncome.customMeta({
      formatter: 'currency',
    }),
    distributableProceedsPostManagementFee: baseFields.distributableProceedsPostManagementFee.customMeta({
      formatter: 'currency',
    }),
    distributableProceedsPostPreferredReturn: baseFields.distributableProceedsPostPreferredReturn.customMeta({
      formatter: 'currency',
    }),
    distributableProceedsPostSecondaryReturn: baseFields.distributableProceedsPostSecondaryReturn.customMeta({
      formatter: 'currency',
    }),
    distributableProceedsPostROC: baseFields.distributableProceedsPostROC.customMeta({
      formatter: 'currency',
    }),
    distributableProceedsPostSLPCapital: baseFields.distributableProceedsPostSLPCapital.customMeta({
      formatter: 'currency',
    }),
    distributableProceedsUnadjusted: baseFields.distributableProceedsUnadjusted.customMeta({
      formatter: 'currency',
    }),
    distributions: baseFields.distributions.customMeta({
      formatter: 'currency',
    }),
    dpi: baseFields.dpi.customMeta({
      formatter: 'multiplier',
    }),
    escrowReceivable: baseFields.escrowReceivable.customMeta({
      formatter: 'currency',
    }),
    fmv: baseFields.fmv.customMeta({
      formatter: 'currency',
    }),
    gpCapital: baseFields.gpCapital.customMeta({
      formatter: 'currency',
    }),
    gpContributions: baseFields.gpContributions.customMeta({
      formatter: 'currency',
    }),
    gpDistributions: baseFields.gpDistributions.customMeta({
      formatter: 'currency',
    }),
    gpIncentive: baseFields.gpIncentive.customMeta({
      formatter: 'currency',
    }),
    gpIncentiveSecondaryReturn: baseFields.secondaryReturnGPIncentive.customMeta({
      formatter: 'currency',
    }),
    gpNonTaxDistribution: baseFields.gpNonTaxDistribution.customMeta({
      formatter: 'currency',
    }),
    gpPortionTotal: baseFields.gpPortionTotal.customMeta({
      formatter: 'currency',
    }),
    gpPremiumCatchup: baseFields.gpPremiumCatchup.customMeta({
      formatter: 'currency',
    }),
    gpROC: baseFields.gpROC.customMeta({
      formatter: 'currency',
    }),
    idleFundsIncomeGP: baseFields.idleFundsIncomeGP.customMeta({
      formatter: 'currency',
    }),
    idleFundsIncomeLP: baseFields.idleFundsIncomeLP.customMeta({
      formatter: 'currency',
    }),
    investmentAmount: baseFields.investmentAmount.customMeta({
      formatter: 'currency',
    }),
    lpContributions: baseFields.lpContributions.customMeta({
      formatter: 'currency',
    }),
    lpDistributions: baseFields.lpDistributions.customMeta({
      formatter: 'currency',
    }),
    lpGpSlitAmount: baseFields.lpGpSlitAmount.customMeta({
      formatter: 'currency',
    }),
    lpNav: baseFields.lpNav.customMeta({
      formatter: 'currency',
    }),
    lpNavFromLpGpSplit: baseFields.lpNavFromLpGpSplit.customMeta({
      formatter: 'currency',
    }),
    lpNavTotal: baseFields.lpNavTotal.customMeta({
      formatter: 'currency',
    }),
    lpROC: baseFields.lpROC.customMeta({
      formatter: 'currency',
    }),
    managementFee: baseFields.managementFee.customMeta({
      formatter: 'currency',
    }),
    managementFeeLP: baseFields.managementFeeLP.customMeta({
      formatter: 'currency',
    }),
    managementFeeRestored: baseFields.managementFeeRestored.customMeta({
      formatter: 'currency',
    }),
    maxGpCatchup: baseFields.maxGpCatchup.customMeta({
      formatter: 'currency',
    }),

    navFromLpGpSplitGp: baseFields.navFromLpGpSplitGp.customMeta({
      formatter: 'currency',
    }),
    navFromLpGpSplitLP: baseFields.navFromLpGpSplitLP.customMeta({
      formatter: 'currency',
    }),
    navFromSecondaryReturnGP: baseFields.navFromSecondaryReturnGP.customMeta({
      formatter: 'currency',
    }),
    navFromSecondaryReturnLP: baseFields.navFromSecondaryReturnLP.customMeta({
      formatter: 'currency',
    }),
    navFromSuperReturnGP: baseFields.navFromSuperReturnGP.customMeta({
      formatter: 'currency',
    }),
    navFromSuperReturnLP: baseFields.navFromSuperReturnLP.customMeta({
      formatter: 'currency',
    }),
    netAssets: baseFields.netAssets.customMeta({
      formatter: 'currency',
    }),
    netIRR: baseFields.netIRR.customMeta({
      formatter: 'percent',
    }),
    netMOIC: baseFields.netMOIC.customMeta({
      formatter: 'multiplier',
    }),
    netReturn: baseFields.netReturn.customMeta({
      formatter: 'currency',
    }),
    profitAtSLPCapital: baseFields.profitAtSLPCapital.customMeta({
      formatter: 'currency',
    }),
    rvpi: baseFields.rvpi.customMeta({
      formatter: 'multiplier',
    }),
    totalValue: baseFields.totalValue.customMeta({
      formatter: 'currency',
    }),
    ttvpi: baseFields.ttvpi.customMeta({
      formatter: 'multiplier',
    }),
  });
}

export type FundMetrics = yup.InferType<ReturnType<typeof fundMetricsFormSchema>>;
